import { GetTokensRequest, toastService } from '@fly/shared';
import {
  GenerateTokenRequest,
  GenerateTokenResponse,
  GeneratedToken,
  Permission,
  Token,
  TokenResponse,
} from '@fly/shared';
import { t } from 'i18next';

import { apiSlice } from './apiSlice';
import { gqlApiSlice } from './gqlApiSlice';

function formatPermission(permission: string): Permission {
  if (permission.split('-').at(-1)?.includes(Permission.READ_WRITE)) {
    return Permission.READ_WRITE;
  }

  return Permission.READ;
}

export const tokensService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Refference for server side pagination
    // getTokens: builder.query<TokenResponseWithPagination, Partial<TableState>>({
    //   query: (tableState) => {
    //     const params = parseTableState(tableState);
    //     return {
    //       api: 'access',
    //       url: `/tokens?${params.toString()}`,
    //       method: 'GET',
    //     };
    //   },
    getTokens: builder.query<Token[], GetTokensRequest>({
      query: (data) => ({
        api: 'access',
        url:
          typeof data === 'object' && data.environmentName
            ? `/tokens/?environment=${data.environmentName}`
            : '/tokens/',
        method: 'GET',
      }),
      transformResponse: (response: TokenResponse) =>
        response.tokens
          ? response.tokens.map((token) => {
              let description = token.description || '';

              if (description.startsWith('fly-env-id:')) {
                description = description.split(':').slice(2).join(':');
              }

              return {
                tokenId: token.token_id,
                scope: formatPermission(token.scope),
                expiresIn: token.expires_in,
                isActive: token.is_active,
                subject: token.subject.split('/').pop() || '',
                issuedAt: new Date(token.issued_at * 1000),
                expiry: new Date(token.expiry * 1000),
                issuer: token.issuer,
                description,
              };
            })
          : [],
      providesTags: ['access-tokens'],
    }),

    generateToken: builder.mutation<GeneratedToken, GenerateTokenRequest>({
      query: (data) => ({
        api: 'access',
        url: '/tokens/',
        method: 'POST',
        data,
      }),
      transformResponse: (response: GenerateTokenResponse) => ({
        tokenId: response.token_id,
        accessToken: response.access_token,
        expiresIn: response.expires_in,
        scope: response.scope,
        tokenType: response.token_type,
        username: response.username,
        referenceToken: response.reference_token,
      }),
      invalidatesTags: ['access-tokens'],
      onQueryStarted: async (data, { queryFulfilled, dispatch }) => {
        await queryFulfilled;

        if (data.environment) {
          dispatch(gqlApiSlice.util.invalidateTags(['environments']));
        }
      },
    }),
    deleteToken: builder.mutation<void, string>({
      query: (id: string) => ({
        api: 'access',
        url: `/tokens/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (id, { queryFulfilled, dispatch }) => {
        await queryFulfilled;
        const msgDeleteSuccess = t('app.common.crud.success.delete', {
          defaultValue: 'Deleted successfully',
        });
        toastService.success(msgDeleteSuccess);
        dispatch(tokensService.util.invalidateTags(['access-tokens']));
        dispatch(gqlApiSlice.util.invalidateTags(['environments']));
      },
    }),
  }),
});

export const {
  useGetTokensQuery,
  useLazyGetTokensQuery,
  useGenerateTokenMutation,
  useDeleteTokenMutation,
} = tokensService;
